import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import SquarespaceLogo from '@99designs/frontend-components/core/images/squarespace-wordmark.svg';
import { CurrencyCode } from '@99designs/i18n';
import { FeaturesList } from '@99designs/spa-common';
import {
    FixedPriceProjectProductsByKeysQuery,
    useFixedPriceProjectProductsByKeysQuery,
} from './FixedPriceProductsByKeys.generated';

export interface ProductFeature {
    title: string;
    description?: string;
}

interface WebPackage {
    id: string;
    productKey: string;
    title: string;
    description: string;
    price: {
        currency: CurrencyCode;
        amountInCents: number;
        isFixed?: boolean;
    };
    features: ProductFeature[];
    content: JSX.Element;
    logo: SVGElement | string;
    featureColor: string;
}

const webPackagesConfig: WebPackage[] = [
    {
        id: 'SQUARESPACE_WEB_ESSENTIAL_V1',
        productKey: 'squarespace-web-essential-v1',
        title: '',
        description: '',
        price: {
            currency: CurrencyCode.USD,
            amountInCents: 0,
            isFixed: true,
        },
        features: [],
        content: <FeaturesList features={[]} />,
        logo: SquarespaceLogo,
        featureColor: '#232323',
    },
    {
        id: 'SQUARESPACE_WEB_COMMERCE_V1',
        productKey: 'squarespace-web-commerce-v1',
        title: '',
        description: '',
        price: {
            currency: CurrencyCode.USD,
            amountInCents: 0,
            isFixed: true,
        },
        features: [],
        content: <FeaturesList features={[]} />,
        logo: SquarespaceLogo,
        featureColor: '#232323',
    },
];

function renderContent(features: ProductFeature[]) {
    const featuresProp = features.map((feature) => {
        return {
            isAvailable: true,
            title: feature.title,
        };
    });
    return <FeaturesList features={featuresProp} />;
}

function populateWebPackages(data: FixedPriceProjectProductsByKeysQuery) {
    // squarespace essential and squarespace commerce
    return webPackagesConfig.map((webPackage) => {
        const product = data.productsByKeys.find(({ key }) => key === webPackage.productKey);

        if (!product || product.__typename !== 'ProductFixedPriceProject') {
            return webPackage;
        }

        const {
            title,
            description,
            details,
            pricing: { amounts },
        } = product;
        const newWebPackage = { ...webPackage };
        const usdAmount = amounts.find((a) => a?.currency === CurrencyCode.USD);

        newWebPackage.title = title;
        newWebPackage.description = description;
        newWebPackage.features = details;
        newWebPackage.content = renderContent(details);
        newWebPackage.price.amountInCents = usdAmount?.amountInCents || 0;
        newWebPackage.price.currency = CurrencyCode.USD;

        return newWebPackage;
    });
}

export function useWebPackageConfig() {
    const [webPackages, setWebPackages] = useState<WebPackage[]>([]);
    const [webPackagesError, setWebPackagesError] = useState<ApolloError>();

    const { loading } = useFixedPriceProjectProductsByKeysQuery({
        variables: {
            keys: webPackagesConfig.map((pkg: WebPackage) => pkg.productKey),
        },
        onCompleted: (data) => {
            setWebPackages(populateWebPackages(data));
        },
        onError: (err) => {
            setWebPackagesError(err);
        },
    });
    return {
        loading,
        webPackages,
        webPackagesError,
    };
}
